import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import {
  Loader2, FlaskConical, Lock, Download,
  AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp,
  ImageIcon, Package, ShoppingCart,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { allStyles } from "@/constants/artStyles";
import { isAdminEmail } from "@/lib/adminAccess";
import {
  ORIENTATIONS,
  FORMAT_CONFIG,
  getPrintDimensions,
  type Orientation,
  type FormatSize,
} from "@/constants/imageFormats";
import {
  FRAME_OPTIONS,
  WOOD_COLORS,
  type FrameType,
} from "@/constants/frameOptions";
import { buildGelatoProductUid } from "@/constants/gelatoProductBuilder";

const ADMIN_TEST_RETENTION_DAYS = 90;

function addDays(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString();
}

function metadataForGeneratedImageUrl(imageUrl: string | undefined) {
  if (!imageUrl) {
    return { generated_storage_provider: null, generated_storage_key: null };
  }

  try {
    const parsed = new URL(imageUrl);
    const supabaseMarker = "/storage/v1/object/public/generated-artworks/";
    const supabaseIndex = parsed.pathname.indexOf(supabaseMarker);
    if (supabaseIndex !== -1) {
      return {
        generated_storage_provider: "supabase" as const,
        generated_storage_key: decodeURIComponent(parsed.pathname.slice(supabaseIndex + supabaseMarker.length)),
      };
    }

    return {
      generated_storage_provider: imageUrl.includes(".r2.dev/") || imageUrl.includes("r2.cloudflarestorage.com") ? "r2-api" as const : null,
      generated_storage_key: decodeURIComponent(parsed.pathname.replace(/^\//, "")),
    };
  } catch {
    return { generated_storage_provider: null, generated_storage_key: null };
  }
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface GenerationResponse {
  success: boolean;
  imageUrl?: string;
  prompt?: string;
  creditsRemaining?: number;
  error?: string;
  debug?: {
    fluxTime?: number;
    uploadTime?: number;
    totalTime?: number;
    settings?: {
      fluxModel: string;
      fluxOutputFormat: "png" | "jpg" | "webp";
      fluxSafetyTolerance: number;
      fluxPromptUpsampling: boolean;
      nanoResolution?: "1K" | "2K" | "4K";
      nanoSafetyFilterLevel?: string;
      skipCreditCharge: boolean;
    };
  };
}

interface DeliveryAddress {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postCode: string;
  country: string;
}

interface Run {
  id: string;
  at: string;
  imageUrl: string;
  orientation: Orientation;
  size: FormatSize;
  style: string;
  nanoResolution: "1K" | "2K" | "4K";
  ratioSent: string;
  gelatoRatio: string;
  totalTime?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const NANO_MODEL = "google/nano-banana-pro";

/**
 * Retourne un base64 JPEG avec l'orientation correcte.
 * createImageBitmap applique l'EXIF automatiquement — même comportement que le navigateur.
 */
async function normalizeImageOrientation(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width  = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.92).split(",")[1];
}

function estimatedNanoPx(res: "1K" | "2K" | "4K"): number {
  return res === "1K" ? 1024 : res === "2K" ? 2048 : 4096;
}

type PrintQuality = "good" | "marginal" | "bad";

function printQuality(nanoPx: number, target: { width: number; height: number }): PrintQuality {
  const ratio = nanoPx / Math.min(target.width, target.height);
  if (ratio >= 0.85) return "good";
  if (ratio >= 0.6)  return "marginal";
  return "bad";
}

const SIZE_LABELS: Record<FormatSize, string> = { small: "Petit", medium: "Moyen", large: "Grand" };

const DEFAULT_ADDRESS: DeliveryAddress = {
  firstName: "Test",
  lastName: "Admin",
  addressLine1: "1 Rue de la Paix",
  addressLine2: "",
  city: "Paris",
  postCode: "75001",
  country: "FR",
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

function QualityBadge({ quality }: { quality: PrintQuality }) {
  if (quality === "good")
    return <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><CheckCircle className="w-3 h-3" /> Qualité print OK</span>;
  if (quality === "marginal")
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><AlertTriangle className="w-3 h-3" /> Qualité marginale</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"><XCircle className="w-3 h-3" /> Résolution insuffisante</span>;
}

// ─── Composant principal ──────────────────────────────────────────────────────

const AdminStudio = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // ── Paramètres utilisateur ──
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [size, setSize] = useState<FormatSize>("medium");
  const [style, setStyle] = useState("impressionist");

  // ── Cadre (exactement comme le flow user) ──
  const [frameType, setFrameType] = useState<FrameType>("none");
  const [frameColor, setFrameColor] = useState<string>("black");

  // ── Paramètres Nano Banana ──
  const [nanoResolution, setNanoResolution] = useState<"1K" | "2K" | "4K">("4K");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [skipCredit, setSkipCredit] = useState(true);

  // ── Génération ──
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<GenerationResponse | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);

  // ── Commande Gelato ──
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [address, setAddress] = useState<DeliveryAddress>(DEFAULT_ADDRESS);
  const [ordering, setOrdering] = useState(false);
  const [orderResult, setOrderResult] = useState<{ success: boolean; gelatoOrderId?: string; productUid?: string; error?: string } | null>(null);

  // ── Auth ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/auth"); return; }
      setUser(session.user);
      setLoadingAuth(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate("/auth");
      else setUser(session.user);
      setLoadingAuth(false);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const isAllowed = useMemo(() => isAdminEmail(user?.email), [user?.email]);

  // ── Format dérivé ──
  const fmt = FORMAT_CONFIG[orientation][size];
  const ratioToSend = fmt.nanoBananaRatio;
  const gelatoRatio = fmt.aspectRatio;
  const printDims = getPrintDimensions(orientation, fmt.dimensions);
  const nanoPx = estimatedNanoPx(nanoResolution);
  const quality = printDims ? printQuality(nanoPx, printDims) : null;
  const ratioMismatch = ratioToSend !== gelatoRatio;

  // ── Couleurs disponibles selon le cadre ──
  const availableColors = frameType === "wood" ? WOOD_COLORS : [];
  useEffect(() => {
    if (availableColors.length > 0) setFrameColor(availableColors[0].id);
  }, [frameType]);

  // ── ProductUID prévisualisé ──
  const previewProductUid = buildGelatoProductUid({ orientation, size, frameType, frameColor });

  // ── Upload image ──
  const onUpload = (file: File | null) => {
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
    setResult(null);
    setOrderResult(null);
    setShowOrderForm(false);
  };

  // ── Génération ──
  const handleGenerate = async () => {
    if (!image || !isAllowed) return;
    setGenerating(true);
    setResult(null);
    setOrderResult(null);
    setShowOrderForm(false);

    try {
      const b64 = await normalizeImageOrientation(image);

      const { data, error } = await supabase.functions.invoke<GenerationResponse>("generate-artwork", {
        body: {
          imageBase64: b64,
          style,
          formatSize: fmt.dimensions,
          aspectRatio: ratioToSend,
          studioOptions: {
            fluxModel: NANO_MODEL,
            fluxOutputFormat: "jpg",
            fluxSafetyTolerance: 2,
            fluxPromptUpsampling: false,
            nanoResolution,
            nanoSafetyFilterLevel: "block_only_high",
            skipCreditCharge: skipCredit,
          },
        },
      });

      if (error) throw new Error(error.message);
      if (!data?.success || !data.imageUrl) throw new Error(data?.error || "Pas d'image retournée");

      setResult(data);
      setRuns((prev) => [{
        id: crypto.randomUUID(),
        at: new Date().toISOString(),
        imageUrl: data.imageUrl!,
        orientation, size, style, nanoResolution,
        ratioSent: ratioToSend,
        gelatoRatio,
        totalTime: data.debug?.totalTime,
      }, ...prev]);
    } catch (e) {
      setResult({ success: false, error: e instanceof Error ? e.message : "Erreur inconnue" });
    } finally {
      setGenerating(false);
    }
  };

  // ── Commande Gelato ──
  const handleOrder = async () => {
    if (!result?.imageUrl || !isAllowed) return;
    setOrdering(true);
    setOrderResult(null);

    try {
      // 1. Créer la commande en base (statut admin_test, pas de paiement)
      const formatSize = `${orientation}_${fmt.dimensions}`;
      const generatedStorageMetadata = metadataForGeneratedImageUrl(result.imageUrl);
      const { data: orderData, error: orderError } = await supabase
        .from("artwork_orders")
        .insert({
          user_id: user!.id,
          format_size: formatSize,
          price_cents: fmt.priceCents,
          selected_style: style,
          generated_image_url: result.imageUrl,
          original_image_url: result.imageUrl,
          ...generatedStorageMetadata,
          generated_retention_reason: "manual_hold",
          generated_expires_at: addDays(ADMIN_TEST_RETENTION_DAYS),
          status: "admin_test",
          payment_status: "admin_test",
          customer_email: user!.email,
          customer_name: `${address.firstName} ${address.lastName}`,
          frame_option: frameType,
          special_instructions: frameType !== "none" ? `Frame color: ${frameColor}.` : "",
          shipping_address: address,
        })
        .select("id")
        .single();

      if (orderError || !orderData) throw new Error(orderError?.message || "Impossible de créer la commande");

      // 2. Envoyer à Gelato directement
      const { data: gelatoData, error: gelatoError } = await supabase.functions.invoke<{ success: boolean; gelatoOrderId?: string; error?: string }>(
        "process-gelato-order",
        { body: { orderId: orderData.id } }
      );

      if (gelatoError) throw new Error(gelatoError.message);
      if (!gelatoData?.success) throw new Error(gelatoData?.error || "Erreur Gelato");

      setOrderResult({
        success: true,
        gelatoOrderId: gelatoData.gelatoOrderId,
        productUid: previewProductUid,
      });
    } catch (e) {
      setOrderResult({ success: false, error: e instanceof Error ? e.message : "Erreur inconnue" });
    } finally {
      setOrdering(false);
    }
  };

  const handleDownload = () => {
    if (!result?.imageUrl) return;
    const a = document.createElement("a");
    a.href = result.imageUrl;
    a.download = `nanobana-${orientation}-${size}-${style}-${Date.now()}.jpg`;
    a.click();
  };

  // ─── Rendus conditionnels ─────────────────────────────────────────────────

  if (loadingAuth || !user) {
    return <div className="min-h-screen pt-20 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…</div>;
  }

  if (!isAllowed) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-6 max-w-xl py-10">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Accès refusé</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <p>Cet email n'est pas dans la liste admin.</p>
              <p>Configure <code>VITE_ADMIN_EMAILS</code> dans ton .env.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── UI principale ────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen pt-20 pb-16 bg-background">
      <div className="container mx-auto px-6 max-w-5xl">

        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-primary" />
            Studio — Test Nano Banana
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Simule exactement le parcours utilisateur et valide la commande Gelato de bout en bout.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Colonne gauche : paramètres ── */}
          <div className="space-y-5">

            {/* 1. Image */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">1. Téléverser une photo</CardTitle></CardHeader>
              <CardContent>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-input rounded-lg p-6 cursor-pointer hover:border-primary/50 transition-colors">
                  {imagePreview
                    ? <img src={imagePreview} alt="Aperçu" className="max-h-48 rounded-md object-contain" />
                    : <><ImageIcon className="w-8 h-8 text-muted-foreground mb-2" /><span className="text-sm text-muted-foreground">Cliquer pour sélectionner</span></>
                  }
                  <Input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0] ?? null)} />
                </label>
              </CardContent>
            </Card>

            {/* 2. Orientation */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">2. Orientation</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {ORIENTATIONS.map((o) => (
                    <button key={o.id} onClick={() => setOrientation(o.id)}
                      className={`rounded-lg border-2 p-3 text-sm font-medium transition-all ${orientation === o.id ? "border-primary bg-primary/5 text-primary" : "border-input hover:border-primary/40"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 3. Format */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">3. Format (taille du poster)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  {(["small", "medium", "large"] as FormatSize[]).map((s) => {
                    const cfg = FORMAT_CONFIG[orientation][s];
                    const dims = getPrintDimensions(orientation, cfg.dimensions);
                    const px = estimatedNanoPx(nanoResolution);
                    const q = dims ? printQuality(px, dims) : null;
                    return (
                      <button key={s} onClick={() => setSize(s)}
                        className={`rounded-lg border-2 p-3 text-left transition-all ${size === s ? "border-primary bg-primary/5" : "border-input hover:border-primary/40"}`}>
                        <div className="font-semibold text-sm">{SIZE_LABELS[s]}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{cfg.label}</div>
                        <div className="text-xs font-mono text-muted-foreground">{cfg.aspectRatio}</div>
                        {q && <div className={`mt-1 w-2 h-2 rounded-full ${q === "good" ? "bg-green-500" : q === "marginal" ? "bg-amber-500" : "bg-red-500"}`} />}
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-md bg-muted/40 border p-3 text-xs space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dimensions du poster</span>
                    <span className="font-medium">{fmt.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ratio Gelato</span>
                    <span className="font-mono font-semibold">{gelatoRatio}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ratio envoyé à Nano Banana</span>
                    <span className={`font-mono font-semibold ${ratioMismatch ? "text-amber-600" : "text-green-600"}`}>
                      {ratioToSend}{!ratioMismatch && " ✓"}
                    </span>
                  </div>
                  {printDims && <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cible print Gelato</span>
                      <span className="font-mono">{printDims.width} × {printDims.height} px</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Résolution NB ({nanoResolution})</span>
                      <span className="font-mono">~{nanoPx} px</span>
                    </div>
                  </>}
                  {quality && <div className="pt-1"><QualityBadge quality={quality} /></div>}
                  {ratioMismatch && (
                    <div className="flex items-start gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>Nano Banana ne supporte pas {gelatoRatio}. Le ratio {ratioToSend} sera utilisé — légère différence de cadrage.</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 4. Style */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">4. Style artistique</CardTitle></CardHeader>
              <CardContent>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={style} onChange={(e) => setStyle(e.target.value)}>
                  {allStyles.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </CardContent>
            </Card>

            {/* 5. Cadre — exactement comme le flow user */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4" /> 5. Finition & cadre
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  {FRAME_OPTIONS.map((f) => (
                    <button key={f.id} onClick={() => setFrameType(f.id)}
                      className={`rounded-lg border-2 p-3 text-left transition-all ${frameType === f.id ? "border-primary bg-primary/5" : "border-input hover:border-primary/40"}`}>
                      <div className="font-semibold text-sm">{f.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {f.priceMultiplier === 1 ? "Prix de base" : `×${f.priceMultiplier}`}
                      </div>
                    </button>
                  ))}
                </div>

                {availableColors.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Couleur du cadre</p>
                    <div className="flex gap-2">
                      {availableColors.map((c) => (
                        <button key={c.id} onClick={() => setFrameColor(c.id)}
                          title={c.name}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${frameColor === c.id ? "border-primary scale-110" : "border-transparent hover:border-muted-foreground"}`}
                          style={{ backgroundColor: c.hex }} />
                      ))}
                      <span className="text-sm self-center text-muted-foreground">
                        {availableColors.find(c => c.id === frameColor)?.name}
                      </span>
                    </div>
                  </div>
                )}

                <div className="rounded-md bg-muted/40 border p-2 text-xs text-muted-foreground font-mono break-all">
                  <span className="text-muted-foreground/60 block mb-0.5">Product UID Gelato :</span>
                  {previewProductUid}
                </div>
              </CardContent>
            </Card>

            {/* 6. Réglages Nano Banana */}
            <Card>
              <button className="w-full flex items-center justify-between px-6 py-4 text-left"
                onClick={() => setShowAdvanced(!showAdvanced)}>
                <span className="text-base font-semibold">Réglages Nano Banana</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs font-mono">{nanoResolution}</Badge>
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>
              {showAdvanced && (
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-2">
                    <Label className="text-sm">Résolution de sortie</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["1K", "2K", "4K"] as const).map((r) => (
                        <button key={r} onClick={() => setNanoResolution(r)}
                          className={`rounded-md border py-2 text-sm font-medium transition-colors ${nanoResolution === r ? "border-primary bg-primary/5 text-primary" : "border-input hover:border-primary/40"}`}>
                          {r}<div className="text-xs text-muted-foreground font-normal">~{estimatedNanoPx(r)}px</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <p className="text-sm font-medium">Ne pas consommer de crédit</p>
                      <p className="text-xs text-muted-foreground">Admin uniquement</p>
                    </div>
                    <Switch checked={skipCredit} onCheckedChange={setSkipCredit} />
                  </div>
                </CardContent>
              )}
            </Card>

            <Button size="lg" className="w-full" disabled={!image || generating} onClick={handleGenerate}>
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération en cours…</>
                : "Générer avec Nano Banana"}
            </Button>
          </div>

          {/* ── Colonne droite : résultat + commande ── */}
          <div className="space-y-5">
            <Card className="sticky top-24">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Résultat</CardTitle>
                  {result?.imageUrl && (
                    <Button variant="outline" size="sm" onClick={handleDownload}>
                      <Download className="w-4 h-4 mr-1.5" /> Télécharger
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">

                {result && !result.success && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{result.error}</div>
                )}

                {result?.imageUrl ? (
                  <img src={result.imageUrl} alt="Résultat" className="w-full rounded-md border object-contain" />
                ) : (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-input rounded-lg py-16 text-muted-foreground">
                    <ImageIcon className="w-8 h-8 mb-2 opacity-40" />
                    <p className="text-sm">Le résultat apparaîtra ici</p>
                  </div>
                )}

                {result?.debug && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {result.debug.totalTime && <Badge>⏱ Total {Math.round(result.debug.totalTime / 1000)}s</Badge>}
                      {result.debug.fluxTime && <Badge variant="secondary">IA {Math.round(result.debug.fluxTime / 1000)}s</Badge>}
                    </div>
                    <div className="rounded-md bg-muted/40 border p-3 text-xs space-y-1">
                      <p className="font-semibold mb-2 text-muted-foreground uppercase tracking-wide text-[10px]">Paramètres envoyés</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <span className="text-muted-foreground">Modèle</span><span className="font-mono truncate">{result.debug.settings?.fluxModel}</span>
                        <span className="text-muted-foreground">Résolution</span><span className="font-mono">{result.debug.settings?.nanoResolution}</span>
                        <span className="text-muted-foreground">Format</span><span className="font-mono">{fmt.dimensions} ({fmt.label})</span>
                        <span className="text-muted-foreground">Ratio envoyé</span><span className="font-mono">{ratioToSend}</span>
                        <span className="text-muted-foreground">Ratio Gelato</span><span className="font-mono">{gelatoRatio}</span>
                        <span className="text-muted-foreground">Cadre</span><span>{frameType === "none" ? "Sans cadre" : `${frameType} / ${frameColor}`}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Section commande Gelato ── */}
                {result?.imageUrl && (
                  <div className="border-t pt-4 space-y-4">
                    <button
                      className="w-full flex items-center justify-between text-sm font-semibold"
                      onClick={() => setShowOrderForm(!showOrderForm)}
                    >
                      <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Tester la commande Gelato</span>
                      {showOrderForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {showOrderForm && (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Adresse de livraison test — sera envoyée à Gelato pour vérifier que le produit et le format passent.
                        </p>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Prénom</Label>
                            <Input className="h-8 text-sm" value={address.firstName} onChange={e => setAddress(a => ({...a, firstName: e.target.value}))} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Nom</Label>
                            <Input className="h-8 text-sm" value={address.lastName} onChange={e => setAddress(a => ({...a, lastName: e.target.value}))} />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Adresse</Label>
                          <Input className="h-8 text-sm" value={address.addressLine1} onChange={e => setAddress(a => ({...a, addressLine1: e.target.value}))} />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Complément (optionnel)</Label>
                          <Input className="h-8 text-sm" value={address.addressLine2} onChange={e => setAddress(a => ({...a, addressLine2: e.target.value}))} />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Code postal</Label>
                            <Input className="h-8 text-sm" value={address.postCode} onChange={e => setAddress(a => ({...a, postCode: e.target.value}))} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Ville</Label>
                            <Input className="h-8 text-sm" value={address.city} onChange={e => setAddress(a => ({...a, city: e.target.value}))} />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Pays (code ISO)</Label>
                          <Input className="h-8 text-sm" value={address.country} onChange={e => setAddress(a => ({...a, country: e.target.value}))} />
                        </div>

                        <Button className="w-full" onClick={handleOrder} disabled={ordering}>
                          {ordering
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi à Gelato…</>
                            : <><ShoppingCart className="w-4 h-4 mr-2" />Envoyer la commande à Gelato</>}
                        </Button>

                        {orderResult && (
                          orderResult.success ? (
                            <div className="rounded-md bg-green-50 border border-green-200 p-3 space-y-2">
                              <p className="text-sm font-semibold text-green-700 flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4" /> Commande acceptée par Gelato !
                              </p>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Gelato Order ID</span>
                                  <span className="font-mono">{orderResult.gelatoOrderId}</span>
                                </div>
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-muted-foreground">Product UID</span>
                                  <span className="font-mono break-all text-[10px]">{orderResult.productUid}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
                              <p className="font-semibold mb-1">Erreur Gelato</p>
                              {orderResult.error}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── Historique ── */}
        {runs.length > 0 && (
          <Card className="mt-8">
            <CardHeader><CardTitle className="text-base">Historique de session ({runs.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {runs.map((run) => (
                  <div key={run.id} className="rounded-lg border overflow-hidden">
                    <img src={run.imageUrl} alt={run.style} className="w-full aspect-square object-cover" />
                    <div className="p-2 space-y-1">
                      <p className="text-xs font-medium truncate">{run.style}</p>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-[10px] px-1">
                          {run.orientation[0].toUpperCase()} · {run.size}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] px-1 font-mono">
                          {run.ratioSent}{run.ratioSent !== run.gelatoRatio && " ≠"}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] px-1">{run.nanoResolution}</Badge>
                        {run.totalTime && <Badge variant="secondary" className="text-[10px] px-1">{Math.round(run.totalTime / 1000)}s</Badge>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminStudio;
